"use client";

import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { trackError, ErrorSeverity } from "@/utils/error-tracker";

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// Use class component for error boundary to catch rendering errors
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error boundary caught error:", error, errorInfo);

    trackError({
      message: error.message || "React rendering error",
      severity: ErrorSeverity.ERROR,
      originalError: error,
      componentStack:
        errorInfo.componentStack || "No component stack available",
    });
  }

  // Additional client-side error handling
  componentDidMount() {
    // Catch unhandled promise rejections
    window.addEventListener("unhandledrejection", this.handlePromiseRejection);

    // Regular error handler (you already had this)
    window.addEventListener("error", this.handleError);
  }

  componentWillUnmount() {
    window.removeEventListener(
      "unhandledrejection",
      this.handlePromiseRejection
    );
    window.removeEventListener("error", this.handleError);
  }

  handlePromiseRejection = (event: PromiseRejectionEvent) => {
    console.error("Unhandled Promise Rejection:", event.reason);

    // Track the error
    trackError({
      message: event.reason?.message || "Unhandled promise rejection",
      severity: ErrorSeverity.ERROR,
      originalError: event.reason,
    });

    this.setState({ hasError: true, error: event.reason });
    event.preventDefault();
  };

  handleError = (event: ErrorEvent) => {
    console.error("Client-side error:", event.error || event.message);

    // Track the error
    trackError({
      message: event.message || "Unhandled client-side error",
      severity: ErrorSeverity.ERROR,
      originalError: event.error,
    });

    this.setState({
      hasError: true,
      error: event.error || new Error(event.message),
    });
    event.preventDefault();
  };

  public render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center">
            <h2 className="text-2xl font-semibold mb-4">
              Something went wrong
            </h2>
            <p className="text-muted-foreground mb-6">
              An unexpected error has occurred. Our team has been notified.
            </p>
            <div className="mb-4 p-4 bg-gray-100 rounded-md text-left max-w-full overflow-auto">
              <code className="text-sm">
                {this.state.error?.message || "Unknown error"}
              </code>
            </div>
            <Button
              onClick={() => {
                this.setState({ hasError: false });
                window.location.reload();
              }}
            >
              Try again
            </Button>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
